import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import "./CampaignsDashboard.css";

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString();
};

const formatNumberOne = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  // Some APIs return rate as 0-1, others as 0-100. Normalize for display.
  const display = num > 1 ? num : num * 100;
  return `${display.toFixed(2)}%`;
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getMetricValue = (row, keys, defaultValue = undefined) => {
  if (!row) return defaultValue;
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  return defaultValue;
};

const getCampaignName = (row) => {
  return (
    row?.CAMPAIGN_NAME ||
    row?.campaign_name ||
    row?.campaign ||
    row?.campaignName ||
    row?.store_name ||
    row?.storeName ||
    row?.account_name ||
    row?.adset ||
    row?.ad_set ||
    "-"
  );
};

const getCampaignType = (row) => {
  const raw = (
    row?.CAMPAIGN_TYPE ||
    row?.campaign_type ||
    row?.campaignType ||
    row?.channel_type ||
    row?.channelType ||
    row?.ad_network ||
    row?.network ||
    getCampaignName(row)
  );
  if (!raw) return "";
  const value = String(raw).toLowerCase();
  const normalized = value.replace(/_/g, " ");
  if (normalized.includes("pmax") || normalized.includes("performance max")) return "PMax";
  if (value.includes("search")) return "Search";
  if (value.includes("display")) return "Display";
  if (value.includes("video")) return "Video";
  return "";
};

const getAssetGroupName = (row) => {
  return (
    row?.ASSET_GROUP ||
    row?.asset_group ||
    row?.assetGroup ||
    row?.ASSET_GROUP_NAME ||
    row?.asset_group_name ||
    row?.asset_group_name ||
    row?.ad_group ||
    row?.adGroup ||
    row?.asset_group ||
    row?.store_name ||
    row?.storeName ||
    "-"
  );
};

const getCreativeUrl = (row) => {
  return (
    row?.CREATIVE ||
    row?.creative ||
    row?.creative_url ||
    row?.image_url ||
    row?.thumbnail ||
    row?.thumb ||
    ""
  );
};
 
export default function CampaignsDashboard() {
  const now = moment();
  const STORE_OPTIONS = [
    { name: "Acura", group: "OEM" },
    { name: "Country Ford", group: "OEM" },
    { name: "Dodge NB", group: "OEM" },
    { name: "Kia", group: "OEM" },
    { name: "Nipawin", group: "OEM" },
    { name: "North Hyundai", group: "OEM" },
    { name: "South 20", group: "OEM" },
    { name: "South Hyundai", group: "OEM" },
    { name: "Volkswagen", group: "OEM" },
    { name: "DN PA", group: "Used" },
    { name: "DN Reg", group: "Used" },
    { name: "DN Sask", group: "Used" },
    { name: "FFUN Cars", group: "Used" },
    { name: "FFUN Group", group: "Used" }
  ];
  const [selectedStoreName, setSelectedStoreName] = useState(STORE_OPTIONS[0]?.name || "");
  const [rangeId, setRangeId] = useState("mtd");
  const [campaignRowsRaw, setCampaignRowsRaw] = useState([]);
  const [assetGroupRowsRaw, setAssetGroupRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assetGroupMenuOpen, setAssetGroupMenuOpen] = useState(false);
  const [visibleAssetGroups, setVisibleAssetGroups] = useState(new Set());
  const assetGroupMenuRef = useRef(null);

  const usedDivisions = ["Used"];

  const dateRanges = useMemo(() => {
    const mtdStart = now.clone().startOf("month");
    const yesterday = now.clone().subtract(1, "day");
    const mtdEnd = yesterday.isBefore(mtdStart) ? mtdStart : yesterday;
    const last7Start = mtdEnd.clone().subtract(6, "days");
    const last30Start = mtdEnd.clone().subtract(29, "days");
    const monthLabel = now.format("MMMM");

    return [
      {
        id: "mtd",
        label: `${monthLabel} MTD (${mtdStart.format("MMM D")}-${mtdEnd.format("D")})`,
        start: mtdStart,
        end: mtdEnd
      },
      {
        id: "last7",
        label: `Last 7 Days (${last7Start.format("MMM D")}-${mtdEnd.format("D")})`,
        start: last7Start,
        end: mtdEnd
      },
      {
        id: "last30",
        label: `Last 30 Days (${last30Start.format("MMM D")}-${mtdEnd.format("D")})`,
        start: last30Start,
        end: mtdEnd
      }
    ];
  }, [now]);

  const activeRange = dateRanges.find((range) => range.id === rangeId) || dateRanges[0];
  const groupedStores = useMemo(
    () => ({
      "OEM Division": STORE_OPTIONS.filter((store) => store.group === "OEM"),
      "Used Division": STORE_OPTIONS.filter((store) => store.group === "Used")
    }),
    []
  );

  const fetchCampaigns = () => {
    if (!activeRange) return;
    if (!selectedStoreName) {
      setError("Please select a store.");
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      start_date: activeRange.start.format("YYYY-MM-DD"),
      end_date: activeRange.end.format("YYYY-MM-DD")
    };

    payload.store_name = selectedStoreName;

    const extractList = (response) => {
      const data = response?.data || {};
      const candidate =
        data?.data?.rows ||
        data?.data?.data ||
        data?.data?.items ||
        data?.rows ||
        data?.items ||
        data?.results ||
        data?.data ||
        data;
      return Array.isArray(candidate) ? candidate : [];
    };

    Promise.allSettled([
      axios.post(Links.googleAdsCampaignsDaily, payload),
      axios.post(Links.googleAdsAssetGroupsDaily, payload)
    ])
      .then(([campaignResult, assetResult]) => {
        const campaignList =
          campaignResult.status === "fulfilled" ? extractList(campaignResult.value) : [];
        const assetList =
          assetResult.status === "fulfilled" ? extractList(assetResult.value) : [];

        setCampaignRowsRaw(campaignList);
        setAssetGroupRowsRaw(assetList);

        if (campaignResult.status === "rejected" && assetResult.status === "rejected") {
          setError("Failed to load campaign data.");
        } else if (campaignResult.status === "rejected") {
          setError("Campaign summary failed to load. Asset group data may still show.");
        } else if (assetResult.status === "rejected") {
          setError("Asset group data failed to load. Campaign summary may still show.");
        }
      })
      .catch((err) => {
        console.log("Campaigns dashboard fetch error:", err);
        setCampaignRowsRaw([]);
        setAssetGroupRowsRaw([]);
        setError("Failed to load campaign data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCampaigns();
  }, [rangeId, selectedStoreName]);

  const campaignRows = useMemo(() => {
    const map = new Map();

    campaignRowsRaw.forEach((row) => {
      const campaign = getCampaignName(row);
      if (!campaign || campaign === "-") return;

      const impressions = toNumber(getMetricValue(row, ["IMPRESSIONS", "impressions", "impr", "Impr"]));
      const clicks = toNumber(getMetricValue(row, ["CLICKS", "clicks", "click"]));
      const cost = toNumber(getMetricValue(row, ["COST", "cost", "SPEND", "spend", "amount_spent"]));
      const conversions = toNumber(getMetricValue(row, ["CONVERSIONS", "conversions", "total_conv", "conv", "Conv"]));
      const leads = toNumber(getMetricValue(row, ["LEADS", "leads", "lead"]));
      const calls = toNumber(getMetricValue(row, ["CALLS", "calls", "call"]));
      const rowCpl = toNumber(getMetricValue(row, ["cpl", "CPL"]));
      const rowCostPerCall = toNumber(getMetricValue(row, ["cost_per_call", "COST_PER_CALL"]));
      const rowCostPerConv = toNumber(getMetricValue(row, ["cost_per_conv", "COST_PER_CONV"]));
      const rowConvRate = toNumber(getMetricValue(row, ["conv_rate", "CONV_RATE"]));

      if (!map.has(campaign)) {
        map.set(campaign, {
          id: row?.CAMPAIGN_ID || row?.campaign_id || row?.id || campaign,
          campaign,
          type: getCampaignType(row),
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          leads: 0,
          calls: 0,
          cpl: rowCpl,
          costPerCall: rowCostPerCall,
          costPerConv: rowCostPerConv,
          convRate: rowConvRate
        });
      }

      const entry = map.get(campaign);
      entry.impressions += impressions;
      entry.clicks += clicks;
      entry.cost += cost;
      entry.conversions += conversions;
      entry.leads += leads;
      entry.calls += calls;
      if (!entry.cpl && rowCpl) entry.cpl = rowCpl;
      if (!entry.costPerCall && rowCostPerCall) entry.costPerCall = rowCostPerCall;
      if (!entry.costPerConv && rowCostPerConv) entry.costPerConv = rowCostPerConv;
      if (!entry.convRate && rowConvRate) entry.convRate = rowConvRate;
      if (!entry.type) {
        entry.type = getCampaignType(row);
      }
    });

    const grouped = Array.from(map.values()).map((entry) => {
      const cpl = entry.cpl || (entry.leads ? entry.cost / entry.leads : 0);
      const costPerCall = entry.costPerCall || (entry.calls ? entry.cost / entry.calls : 0);
      const costPerConv = entry.costPerConv || (entry.conversions ? entry.cost / entry.conversions : 0);
      const convRate = entry.convRate || (entry.clicks ? entry.conversions / entry.clicks : 0);

      return {
        ...entry,
        cpl,
        costPerCall,
        costPerConv,
        convRate
      };
    });
    return grouped.sort((a, b) => b.cost - a.cost);
  }, [campaignRowsRaw]);

  const campaignTotals = useMemo(() => {
    return campaignRows.reduce(
      (acc, row) => {
        acc.impressions += row.impressions;
        acc.clicks += row.clicks;
        acc.cost += row.cost;
        acc.conversions += row.conversions;
        acc.leads += row.leads;
        acc.calls += row.calls;
        return acc;
      },
      { impressions: 0, clicks: 0, cost: 0, conversions: 0, leads: 0, calls: 0 }
    );
  }, [campaignRows]);

  const assetGroups = useMemo(() => {
    const groupMap = new Map();

    assetGroupRowsRaw.forEach((row) => {
      const groupName = getAssetGroupName(row);
      if (!groupName || groupName === "-") return;

      const impressions = toNumber(getMetricValue(row, ["IMPRESSIONS", "impressions", "impr", "Impr"]));
      const clicks = toNumber(getMetricValue(row, ["CLICKS", "clicks", "click"]));
      const cost = toNumber(getMetricValue(row, ["COST", "cost", "spend", "SPEND", "amount_spent"]));
      const leads = toNumber(getMetricValue(row, ["LEADS", "leads", "lead"]));
      const calls = toNumber(getMetricValue(row, ["CALLS", "calls", "call"]));
      const conversions = toNumber(getMetricValue(row, ["CONVERSIONS", "conversions", "total_conv", "conv", "Conv"]));
      const rowCpl = toNumber(getMetricValue(row, ["cpl", "CPL"]));
      const rowCostPerCall = toNumber(getMetricValue(row, ["cost_per_call", "COST_PER_CALL"]));
      const rowConvRate = toNumber(getMetricValue(row, ["conv_rate", "CONV_RATE"]));

      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, {
          assetGroup: groupName,
          impressions: 0,
          clicks: 0,
          cost: 0,
          leads: 0,
          calls: 0,
          conversions: 0,
          creative: getCreativeUrl(row),
          cpl: rowCpl,
          costPerCall: rowCostPerCall,
          convRate: rowConvRate
        });
      }

      const entry = groupMap.get(groupName);
      entry.impressions += impressions;
      entry.clicks += clicks;
      entry.cost += cost;
      entry.leads += leads;
      entry.calls += calls;
      entry.conversions += conversions;
      if (!entry.cpl && rowCpl) entry.cpl = rowCpl;
      if (!entry.costPerCall && rowCostPerCall) entry.costPerCall = rowCostPerCall;
      if (!entry.convRate && rowConvRate) entry.convRate = rowConvRate;
      if (!entry.creative && getCreativeUrl(row)) {
        entry.creative = getCreativeUrl(row);
      }
    });

    return Array.from(groupMap.values());
  }, [assetGroupRowsRaw]);

  useEffect(() => {
    setVisibleAssetGroups(new Set(assetGroups.map((group) => group.assetGroup)));
  }, [assetGroups]);

  useEffect(() => {
    if (!assetGroupMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (assetGroupMenuRef.current && !assetGroupMenuRef.current.contains(event.target)) {
        setAssetGroupMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [assetGroupMenuOpen]);

  const visibleAssetGroupRows = useMemo(() => {
    if (!assetGroups.length) return [];
    return assetGroups.filter((group) => visibleAssetGroups.has(group.assetGroup));
  }, [assetGroups, visibleAssetGroups]);

  const assetGroupLabel = useMemo(() => {
    if (!assetGroups.length) return "All Asset Groups";
    const total = assetGroups.length;
    const visibleCount = visibleAssetGroups.size;
    if (visibleCount === total) return "All Asset Groups";
    if (visibleCount === 0) return "None selected";
    return `${visibleCount} of ${total} assets`;
  }, [assetGroups.length, visibleAssetGroups]);

  const totalsCpl = campaignTotals.leads ? campaignTotals.cost / campaignTotals.leads : 0;
  const totalsCostPerCall = campaignTotals.calls ? campaignTotals.cost / campaignTotals.calls : 0;
  const totalsCostPerConv = campaignTotals.conversions ? campaignTotals.cost / campaignTotals.conversions : 0;
  const totalsConvRate = campaignTotals.clicks ? campaignTotals.conversions / campaignTotals.clicks : 0;

  const assetGroupTotals = visibleAssetGroupRows.reduce(
    (acc, row) => {
      acc.impressions += row.impressions;
      acc.clicks += row.clicks;
      acc.cost += row.cost;
      acc.leads += row.leads;
      acc.calls += row.calls;
      acc.conversions += row.conversions;
      return acc;
    },
    { impressions: 0, clicks: 0, cost: 0, leads: 0, calls: 0, conversions: 0 }
  );

  return (
    <div className="campaigns-dashboard">
      <header className="campaigns-header">
        <h1>
          <span>FFUN</span> - Campaign Performance Dashboard
        </h1>
        <div className="campaigns-header-meta">
          <div>{activeRange?.label}</div>
          <div>Google Ads - Campaign Level</div>
        </div>
      </header>

      <div className="campaigns-container">
        <div className="campaigns-controls">
          <div className="campaigns-control">
            <label htmlFor="campaign-store">Dealership:</label>
            <select
              id="campaign-store"
              value={selectedStoreName}
              onChange={(event) => setSelectedStoreName(event.target.value)}
            >
              <option value="">Select Store</option>
              {Object.entries(groupedStores).map(([groupLabel, storesInGroup]) =>
                storesInGroup.length ? (
                  <optgroup key={groupLabel} label={groupLabel}>
                    {storesInGroup.map((store) => (
                      <option key={store.name} value={store.name}>
                        {store.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null
              )}
            </select>
          </div>
          <div className="campaigns-control">
            <label htmlFor="campaign-range">Date Range:</label>
            <select
              id="campaign-range"
              value={rangeId}
              onChange={(event) => setRangeId(event.target.value)}
            >
              {dateRanges.map((range) => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="campaigns-error">{error}</div>}
        {loading && <div className="campaigns-loading">Loading campaign data...</div>}

        <div className="campaigns-section-title">Campaign Performance</div>
        <div className="campaigns-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Impr</th>
                <th>Clicks</th>
                <th>Cost</th>
                <th>Conv</th>
                <th>Leads</th>
                <th>Calls</th>
                <th>CPL</th>
                <th>Cost/Call</th>
                <th>Cost/Conv</th>
                <th>Conv Rate</th>
              </tr>
            </thead>
            <tbody>
              {campaignRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={11} className="campaigns-empty">
                    No campaign data found.
                  </td>
                </tr>
              ) : (
                campaignRows.map((row, index) => (
                  <tr key={`${row.id}-${index}`}>
                    <td>
                      <div className="campaigns-name">
                        <span>{row.campaign}</span>
                        {row.type && (
                          <span
                            className={`campaigns-badge campaigns-badge--${row.type.toLowerCase()}`}
                          >
                            {row.type}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{formatNumber(row.impressions)}</td>
                    <td>{formatNumber(row.clicks)}</td>
                    <td>{formatCurrency(row.cost)}</td>
                    <td>{formatNumberOne(row.conversions)}</td>
                    <td>{formatNumberOne(row.leads)}</td>
                    <td>{formatNumberOne(row.calls)}</td>
                    <td>{formatCurrency(row.cpl)}</td>
                    <td>{formatCurrency(row.costPerCall)}</td>
                    <td>{formatCurrency(row.costPerConv)}</td>
                    <td>{formatPercent(row.convRate)}</td>
                  </tr>
                ))
              )}
              {campaignRows.length > 0 && (
                <tr className="campaigns-total">
                  <td>Total</td>
                  <td>{formatNumber(campaignTotals.impressions)}</td>
                  <td>{formatNumber(campaignTotals.clicks)}</td>
                  <td>{formatCurrency(campaignTotals.cost)}</td>
                  <td>{formatNumberOne(campaignTotals.conversions)}</td>
                  <td>{formatNumberOne(campaignTotals.leads)}</td>
                  <td>{formatNumberOne(campaignTotals.calls)}</td>
                  <td>{formatCurrency(totalsCpl)}</td>
                  <td>{formatCurrency(totalsCostPerCall)}</td>
                  <td>{formatCurrency(totalsCostPerConv)}</td>
                  <td>{formatPercent(totalsConvRate)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="campaigns-section-header">
          <div className="campaigns-section-title">PMax Asset Group Performance</div>
          <div className="campaigns-toggle" ref={assetGroupMenuRef}>
            <button
              type="button"
              className="campaigns-toggle-btn"
              onClick={() => setAssetGroupMenuOpen((prev) => !prev)}
            >
              {assetGroupLabel}
            </button>
            <div className={`campaigns-toggle-menu ${assetGroupMenuOpen ? "open" : ""}`}>
              <div className="campaigns-toggle-actions">
                <button
                  type="button"
                  onClick={() =>
                    setVisibleAssetGroups(new Set(assetGroups.map((group) => group.assetGroup)))
                  }
                >
                  Show All
                </button>
                <button type="button" onClick={() => setVisibleAssetGroups(new Set())}>
                  Hide All
                </button>
              </div>
              {assetGroups.map((group) => {
                const checked = visibleAssetGroups.has(group.assetGroup);
                return (
                  <label className="campaigns-toggle-item" key={group.assetGroup}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(visibleAssetGroups);
                        if (checked) {
                          next.delete(group.assetGroup);
                        } else {
                          next.add(group.assetGroup);
                        }
                        setVisibleAssetGroups(next);
                      }}
                    />
                    {group.assetGroup}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="campaigns-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Asset Group</th>
                <th>Impr</th>
                <th>Clicks</th>
                <th>Cost</th>
                <th>Leads</th>
                <th>Calls</th>
                <th>CPL</th>
                <th>Cost/Call</th>
                <th>Conv Rate</th>
                <th>Creative</th>
              </tr>
            </thead>
            <tbody>
              {assetGroups.length === 0 && !loading ? (
                <tr>
                  <td colSpan={10} className="campaigns-empty">
                    No asset group data found.
                  </td>
                </tr>
              ) : visibleAssetGroupRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="campaigns-empty">
                    No asset groups selected.
                  </td>
                </tr>
              ) : (
                visibleAssetGroupRows.map((row) => {
                  const cpl = row.cpl || (row.leads ? row.cost / row.leads : 0);
                  const costPerCall = row.costPerCall || (row.calls ? row.cost / row.calls : 0);
                  const convRate = row.convRate || (row.clicks ? row.conversions / row.clicks : 0);

                  return (
                    <tr key={row.assetGroup}>
                      <td>{row.assetGroup}</td>
                      <td>{formatNumber(row.impressions)}</td>
                      <td>{formatNumber(row.clicks)}</td>
                      <td>{formatCurrency(row.cost)}</td>
                      <td>{formatNumberOne(row.leads)}</td>
                      <td>{formatNumberOne(row.calls)}</td>
                      <td>{formatCurrency(cpl)}</td>
                      <td>{formatCurrency(costPerCall)}</td>
                      <td>{formatPercent(convRate)}</td>
                      <td className="campaigns-creative-cell">
                        {row.creative ? (
                          <img
                            className="campaigns-creative"
                            src={row.creative}
                            alt={`${row.assetGroup} creative`}
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <span className="campaigns-creative-placeholder">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
              {visibleAssetGroupRows.length > 0 && (
                <tr className="campaigns-total">
                  <td>
                    Total ({visibleAssetGroupRows.length} asset{visibleAssetGroupRows.length !== 1 ? "s" : ""})
                  </td>
                  <td>{formatNumber(assetGroupTotals.impressions)}</td>
                  <td>{formatNumber(assetGroupTotals.clicks)}</td>
                  <td>{formatCurrency(assetGroupTotals.cost)}</td>
                  <td>{formatNumberOne(assetGroupTotals.leads)}</td>
                  <td>{formatNumberOne(assetGroupTotals.calls)}</td>
                  <td>
                    {assetGroupTotals.leads
                      ? formatCurrency(assetGroupTotals.cost / assetGroupTotals.leads)
                      : "-"}
                  </td>
                  <td>
                    {assetGroupTotals.calls
                      ? formatCurrency(assetGroupTotals.cost / assetGroupTotals.calls)
                      : "-"}
                  </td>
                  <td>
                    {assetGroupTotals.clicks
                      ? formatPercent(assetGroupTotals.conversions / assetGroupTotals.clicks)
                      : "-"}
                  </td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="campaigns-footer">
          Source: Google Ads API - pulled {now.format("YYYY-MM-DD")} | Date range: {activeRange?.start.format("YYYY-MM-DD")} to {activeRange?.end.format("YYYY-MM-DD")} | Leads = Conv - Call Conv | Campaigns and asset groups with &lt;$1 spend excluded
        </div>
      </div>
    </div>
  );
}
